/**
 * The one pending hand-off to WhatsApp or the dialler (KTD9).
 *
 * Tapping Message or Call on a follow-up records nothing. It stores what was
 * tapped in `sessionStorage`, and the outcome prompt asks what happened when
 * the realtor comes back: when the page becomes visible again, or on mount,
 * because a phone often reloads a tab that sat in the background.
 *
 * A new tap replaces the stored hand-off. One older than two hours is thrown
 * away unread, so a prompt never asks about a tap from this morning.
 *
 * A Message tap by a realtor whose WhatsApp is connected is stored as
 * `captured` (KTD13): the queue checks whether WhatsApp recorded the sent
 * message, and asks only when it finds nothing. A hand-off stored without
 * the mark is asked about as before.
 */

import { useCallback, useEffect, useState } from "react";
import type { FollowUpReasonType } from "@/features/realtor/services/realtor-crm-api.service";

export const HANDOFF_STORAGE_KEY = "finishd.realtor.followUpHandoff";
export const HANDOFF_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export type HandoffAction = "message" | "call";

export interface PendingHandoff {
	contactId: string;
	contactName: string;
	action: HandoffAction;
	/** The leading reason's lead; the logged message is tagged with it. */
	leadId: string | null;
	reasons: FollowUpReasonType[];
	/** The text WhatsApp was opened with; "Sent" records it. */
	message: string;
	/** Set when the leading reason is a listing match: "Sent" links it. */
	listingId: string | null;
	/** `Date.now()` at the tap. */
	createdAt: number;
	/** Message with WhatsApp connected: check for the sent message first. */
	captured?: boolean;
	/** The two-minute check after the first return found nothing. */
	checkWindowEnded?: boolean;
}

function isHandoff(value: unknown): value is PendingHandoff {
	if (typeof value !== "object" || value === null) return false;
	const h = value as Record<string, unknown>;
	return (
		typeof h.contactId === "string" &&
		typeof h.contactName === "string" &&
		(h.action === "message" || h.action === "call") &&
		(h.leadId === null || typeof h.leadId === "string") &&
		Array.isArray(h.reasons) &&
		typeof h.message === "string" &&
		(h.listingId === null || typeof h.listingId === "string") &&
		typeof h.createdAt === "number" &&
		(h.captured === undefined || typeof h.captured === "boolean") &&
		(h.checkWindowEnded === undefined ||
			typeof h.checkWindowEnded === "boolean")
	);
}

export function clearHandoff(): void {
	try {
		sessionStorage.removeItem(HANDOFF_STORAGE_KEY);
	} catch {
		// Storage blocked: there is nothing stored to clear.
	}
}

/**
 * The stored hand-off, or null when there is none, it cannot be read, or it
 * is older than two hours. A stale or unreadable one is removed.
 */
export function readHandoff(now: number = Date.now()): PendingHandoff | null {
	let raw: string | null;
	try {
		raw = sessionStorage.getItem(HANDOFF_STORAGE_KEY);
	} catch {
		return null;
	}
	if (raw === null) return null;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		parsed = null;
	}
	if (!isHandoff(parsed) || now - parsed.createdAt > HANDOFF_MAX_AGE_MS) {
		clearHandoff();
		return null;
	}
	return parsed;
}

export function storeHandoff(
	handoff: Omit<PendingHandoff, "createdAt">,
	now: number = Date.now(),
): void {
	try {
		sessionStorage.setItem(
			HANDOFF_STORAGE_KEY,
			JSON.stringify({ ...handoff, createdAt: now }),
		);
	} catch {
		// Storage blocked: the prompt cannot ask later, and nothing breaks.
	}
}

/**
 * Records on the stored hand-off that its two-minute check ended, so a later
 * return checks once instead of starting another window. Writes nothing when
 * the stored hand-off is a different tap or is gone.
 */
export function markCheckWindowEnded(
	handoff: PendingHandoff,
	now: number = Date.now(),
): void {
	const stored = readHandoff(now);
	if (!isSameTap(stored, handoff)) return;
	try {
		sessionStorage.setItem(
			HANDOFF_STORAGE_KEY,
			JSON.stringify({ ...stored, checkWindowEnded: true }),
		);
	} catch {
		// Storage blocked: a later return runs the full window again.
	}
}

/** Both are the same tap: same person, same moment. */
export function isSameTap(
	a: PendingHandoff | null,
	b: PendingHandoff | null,
): boolean {
	return (
		a !== null &&
		b !== null &&
		a.contactId === b.contactId &&
		a.createdAt === b.createdAt
	);
}

export interface PendingHandoffControls {
	/** The stored hand-off, read again on mount and on each return; or null. */
	pending: PendingHandoff | null;
	/** Store a new tap, replacing any earlier one. Opens nothing yet. */
	start: (handoff: Omit<PendingHandoff, "createdAt">) => void;
	/** Forget the hand-off and close the prompt. */
	finish: () => void;
}

export function usePendingHandoff(): PendingHandoffControls {
	const [pending, setPending] = useState<PendingHandoff | null>(null);

	useEffect(() => {
		// On mount: the tab may have been reloaded while the realtor was away.
		setPending(readHandoff());
		const onVisibilityChange = (): void => {
			if (document.visibilityState !== "visible") return;
			setPending(readHandoff());
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, []);

	const start = useCallback(
		(handoff: Omit<PendingHandoff, "createdAt">): void => {
			storeHandoff(handoff);
		},
		[],
	);

	const finish = useCallback((): void => {
		clearHandoff();
		setPending(null);
	}, []);

	return { pending, start, finish };
}
